import type {
  CommonModel,
  IndeedSubscriptionAdminResponse,
  IndeedSubscriptionAdminSubscription,
  IndeedSubscriptionGraphQLResponse,
} from './indeed.types';

export const getActiveSubscriptionFromAdminResponse = (
  subscriptionAdmin: IndeedSubscriptionAdminResponse | null,
): IndeedSubscriptionAdminSubscription | undefined => {
  const activeSubscription = subscriptionAdmin?.subscriptions.find((subscription) => subscription.status === 'ACTIVE');

  if (!subscriptionAdmin?.hasActiveSubscription && !activeSubscription) return undefined;

  return activeSubscription;
};

export const getHasExpiredSubscriptionFromAdminResponse = (
  subscriptionAdmin: IndeedSubscriptionAdminResponse | null,
): boolean => {
  return Boolean(subscriptionAdmin?.subscriptions.some((subscription) => subscription.status === 'EXPIRED'));
};

export const getSubscriptionAdminCreditBalance = (subscription: IndeedSubscriptionAdminSubscription): number => {
  return subscription.contactsRemaining ?? subscription.subscriptionModel?.contactsRemaining ?? 0;
};

export const getSubscriptionAdminEmail = (
  subscriptionAdmin: IndeedSubscriptionAdminResponse | null,
  subscription: IndeedSubscriptionAdminSubscription | undefined,
): string => {
  return (
    subscriptionAdmin?.potentialAssignees.find((assignee) => assignee.accountKey === subscription?.assigneeKey)
      ?.emailAddress ?? ''
  );
};

export const getAdminFallbackSubscriptionState = (
  commonModel: CommonModel,
  subscriptionAdmin: IndeedSubscriptionAdminResponse | null,
): {
  hasActiveSubscription: true;
  hasExpiredSubscription: boolean;
  creditBalance: number;
  email: string;
} | null => {
  const activeAdminSubscription = getActiveSubscriptionFromAdminResponse(subscriptionAdmin);

  if (!activeAdminSubscription) return null;

  return {
    hasActiveSubscription: true,
    hasExpiredSubscription: getHasExpiredSubscriptionFromAdminResponse(subscriptionAdmin),
    creditBalance: getSubscriptionAdminCreditBalance(activeAdminSubscription),
    email: commonModel.emailAddress ?? getSubscriptionAdminEmail(subscriptionAdmin, activeAdminSubscription),
  };
};

const normalizeGraphQLSubscriptionStatus = (status: string | null | undefined): string => {
  if (status?.startsWith('ACTIVE')) return 'ACTIVE';
  if (status?.includes('EXPIRED')) return 'EXPIRED';

  return 'UNKNOWN';
};

export const convertSubscriptionGraphQLResponseToAdminResponse = (
  response: IndeedSubscriptionGraphQLResponse,
): IndeedSubscriptionAdminResponse => {
  const email = response.data.currentEmployerUser.account.accountProfile.email.emailAddress;
  const contactsRemaining =
    response.data.talentEntitlements.entitlements.find((entitlement) => entitlement.action === 'contact')?.quantity ??
    0;
  const subscriptions = response.data.talentSubscriptions.subscriptions.map((subscription) => ({
    assigneeKey: email,
    status: normalizeGraphQLSubscriptionStatus(subscription?.status),
    contactsRemaining,
    subscriptionModel: {
      contactsRemaining,
    },
  }));

  return {
    subscriptions,
    sharedSubscription: {
      contactsRemaining,
    },
    potentialAssignees: [
      {
        emailAddress: email,
      },
    ],
    hasActiveSubscription: subscriptions.some((subscription) => subscription.status === 'ACTIVE'),
  };
};
